import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion} from 'framer-motion';
import { DateTime } from 'luxon';
import { calculateBirthChartWithLibrary } from '../../astro/horoscopeCalculator';
import AsyncSelect from 'react-select/async';
import {
  Clock,
  MapPin,
  Calendar,
  User,
  Sparkles,
  ArrowRight,
  RefreshCw,
  Info,
  ArrowLeft,
  Search,
  Save,
  ChevronDown
} from 'lucide-react';
import { useFirebase } from '../../context/firebaseContext';
import { useAuth } from '../../context/authContext';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';

// Copy the city list and timezone map from birthchartform
const majorCities = [
  { name: "New York, NY, USA", lat: 40.7128, lon: -74.0060 },
  { name: "Los Angeles, CA, USA", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago, IL, USA", lat: 41.8781, lon: -87.6298 },
  { name: "Houston, TX, USA", lat: 29.7604, lon: -95.3698 },
  { name: "Phoenix, AZ, USA", lat: 33.4484, lon: -112.0740 },
  { name: "Philadelphia, PA, USA", lat: 39.9526, lon: -75.1652 },
  { name: "San Antonio, TX, USA", lat: 29.4241, lon: -98.4936 },
  { name: "San Diego, CA, USA", lat: 32.7157, lon: -117.1611 },
  { name: "Dallas, TX, USA", lat: 32.7767, lon: -96.7970 },
  { name: "San Jose, CA, USA", lat: 37.3382, lon: -121.8863 },
  { name: "London, UK", lat: 51.5074, lon: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lon: 2.3522 },
  { name: "Berlin, Germany", lat: 52.5200, lon: 13.4050 },
  { name: "Madrid, Spain", lat: 40.4168, lon: -3.7038 },
  { name: "Rome, Italy", lat: 41.9028, lon: 12.4964 },
  { name: "Amsterdam, Netherlands", lat: 52.3676, lon: 4.9041 },
  { name: "Vienna, Austria", lat: 48.2082, lon: 16.3738 },
  { name: "Prague, Czech Republic", lat: 50.0755, lon: 14.4378 },
  { name: "Budapest, Hungary", lat: 47.4979, lon: 19.0402 },
  { name: "Warsaw, Poland", lat: 52.2297, lon: 21.0122 },
  { name: "Moscow, Russia", lat: 55.7558, lon: 37.6176 },
  { name: "Tokyo, Japan", lat: 35.6762, lon: 139.6503 },
  { name: "Seoul, South Korea", lat: 37.5665, lon: 126.9780 },
  { name: "Beijing, China", lat: 39.9042, lon: 116.4074 },
  { name: "Shanghai, China", lat: 31.2304, lon: 121.4737 },
  { name: "Hong Kong", lat: 22.3193, lon: 114.1694 },
  { name: "Singapore", lat: 1.3521, lon: 103.8198 },
  { name: "Bangkok, Thailand", lat: 13.7563, lon: 100.5018 },
  { name: "Jakarta, Indonesia", lat: -6.2088, lon: 106.8456 },
  { name: "Kuala Lumpur, Malaysia", lat: 3.1390, lon: 101.6869 },
  { name: "Manila, Philippines", lat: 14.5995, lon: 120.9842 },
  { name: "Ho Chi Minh City, Vietnam", lat: 10.8231, lon: 106.6297 },
  { name: "Mumbai, India", lat: 19.0760, lon: 72.8777 },
  { name: "Delhi, India", lat: 28.7041, lon: 77.1025 },
  { name: "Bangalore, India", lat: 12.9716, lon: 77.5946 },
  { name: "Chennai, India", lat: 13.0827, lon: 80.2707 },
  { name: "Kolkata, India", lat: 22.5726, lon: 88.3639 },
  { name: "Hyderabad, India", lat: 17.3850, lon: 78.4867 },
  { name: "Pune, India", lat: 18.5204, lon: 73.8567 },
  { name: "Ahmedabad, India", lat: 23.0225, lon: 72.5714 },
  { name: "Sydney, Australia", lat: -33.8688, lon: 151.2093 },
  { name: "Melbourne, Australia", lat: -37.8136, lon: 144.9631 },
  { name: "Brisbane, Australia", lat: -27.4698, lon: 153.0251 },
  { name: "Perth, Australia", lat: -31.9505, lon: 115.8605 },
  { name: "Adelaide, Australia", lat: -34.9285, lon: 138.6007 },
  { name: "Toronto, Canada", lat: 43.6532, lon: -79.3832 },
  { name: "Montreal, Canada", lat: 45.5017, lon: -73.5673 },
  { name: "Vancouver, Canada", lat: 49.2827, lon: -123.1207 },
  { name: "Calgary, Canada", lat: 51.0447, lon: -114.0719 },
  { name: "Edmonton, Canada", lat: 53.5461, lon: -113.4938 },
  { name: "Ottawa, Canada", lat: 45.4215, lon: -75.6972 },
  { name: "Mexico City, Mexico", lat: 19.4326, lon: -99.1332 },
  { name: "Guadalajara, Mexico", lat: 20.6597, lon: -103.3496 },
  { name: "Monterrey, Mexico", lat: 25.6866, lon: -100.3161 },
  { name: "Puebla, Mexico", lat: 19.0413, lon: -98.2062 },
  { name: "São Paulo, Brazil", lat: -23.5505, lon: -46.6333 },
  { name: "Rio de Janeiro, Brazil", lat: -22.9068, lon: -43.1729 },
  { name: "Brasília, Brazil", lat: -15.7942, lon: -47.8822 },
  { name: "Salvador, Brazil", lat: -12.9714, lon: -38.5011 },
  { name: "Fortaleza, Brazil", lat: -3.7319, lon: -38.5267 },
  { name: "Belo Horizonte, Brazil", lat: -19.9167, lon: -43.9345 },
  { name: "Buenos Aires, Argentina", lat: -34.6118, lon: -58.3960 },
  { name: "Córdoba, Argentina", lat: -31.4201, lon: -64.1888 },
  { name: "Rosario, Argentina", lat: -32.9468, lon: -60.6393 },
  { name: "Santiago, Chile", lat: -33.4489, lon: -70.6693 },
  { name: "Valparaíso, Chile", lat: -33.0472, lon: -71.6127 },
  { name: "Lima, Peru", lat: -12.0464, lon: -77.0428 },
  { name: "Arequipa, Peru", lat: -16.4090, lon: -71.5375 },
  { name: "Bogotá, Colombia", lat: 4.7110, lon: -74.0721 },
  { name: "Medellín, Colombia", lat: 6.2442, lon: -75.5812 },
  { name: "Cali, Colombia", lat: 3.4516, lon: -76.5320 },
  { name: "Caracas, Venezuela", lat: 10.4806, lon: -66.9036 },
  { name: "Maracaibo, Venezuela", lat: 10.6427, lon: -71.6125 },
  { name: "Quito, Ecuador", lat: -0.2299, lon: -78.5249 },
  { name: "Guayaquil, Ecuador", lat: -2.1894, lon: -79.8891 },
  { name: "Montevideo, Uruguay", lat: -34.9011, lon: -56.1645 },
  { name: "Asunción, Paraguay", lat: -25.2637, lon: -57.5759 },
  { name: "La Paz, Bolivia", lat: -16.4897, lon: -68.1193 },
  { name: "Santa Cruz, Bolivia", lat: -17.7833, lon: -63.1821 },
  { name: "Cape Town, South Africa", lat: -33.9249, lon: 18.4241 },
  { name: "Johannesburg, South Africa", lat: -26.2041, lon: 28.0473 },
  { name: "Durban, South Africa", lat: -29.8587, lon: 31.0218 },
  { name: "Pretoria, South Africa", lat: -25.7479, lon: 28.2293 },
  { name: "Port Elizabeth, South Africa", lat: -33.7139, lon: 25.5207 },
  { name: "Cairo, Egypt", lat: 30.0444, lon: 31.2357 },
  { name: "Alexandria, Egypt", lat: 31.2001, lon: 29.9187 },
  { name: "Giza, Egypt", lat: 30.0131, lon: 31.2089 },
  { name: "Lagos, Nigeria", lat: 6.5244, lon: 3.3792 },
  { name: "Kano, Nigeria", lat: 11.9914, lon: 8.5317 },
  { name: "Ibadan, Nigeria", lat: 7.3964, lon: 3.8867 },
  { name: "Kaduna, Nigeria", lat: 10.5222, lon: 7.4384 },
  { name: "Port Harcourt, Nigeria", lat: 4.8156, lon: 7.0498 },
  { name: "Nairobi, Kenya", lat: -1.2921, lon: 36.8219 },
  { name: "Mombasa, Kenya", lat: -4.0435, lon: 39.6682 },
  { name: "Kisumu, Kenya", lat: -0.1022, lon: 34.7617 },
  { name: "Addis Ababa, Ethiopia", lat: 9.0320, lon: 38.7486 },
  { name: "Dar es Salaam, Tanzania", lat: -6.8235, lon: 39.2695 },
  { name: "Mwanza, Tanzania", lat: -2.5167, lon: 32.9000 },
  { name: "Kampala, Uganda", lat: 0.3476, lon: 32.5825 },
  { name: "Khartoum, Sudan", lat: 15.5007, lon: 32.5599 },
  { name: "Casablanca, Morocco", lat: 33.5731, lon: -7.5898 },
  { name: "Rabat, Morocco", lat: 34.0209, lon: -6.8416 },
  { name: "Fez, Morocco", lat: 34.0181, lon: -5.0078 },
  { name: "Marrakech, Morocco", lat: 31.6295, lon: -7.9811 },
  { name: "Tunis, Tunisia", lat: 36.8065, lon: 10.1815 },
  { name: "Sfax, Tunisia", lat: 34.7477, lon: 10.7662 },
  { name: "Algiers, Algeria", lat: 36.7538, lon: 3.0588 },
  { name: "Oran, Algeria", lat: 35.6971, lon: -0.6359 },
  { name: "Tehran, Iran", lat: 35.6892, lon: 51.3890 },
  { name: "Mashhad, Iran", lat: 36.2972, lon: 59.6067 },
  { name: "Isfahan, Iran", lat: 32.6546, lon: 51.6680 },
  { name: "Tabriz, Iran", lat: 38.0962, lon: 46.2738 },
  { name: "Shiraz, Iran", lat: 29.5916, lon: 52.5836 },
  { name: "Karaj, Iran", lat: 35.8400, lon: 50.9391 },
  { name: "Qom, Iran", lat: 34.6416, lon: 50.8746 },
  { name: "Kermanshah, Iran", lat: 34.3277, lon: 47.0778 },
  { name: "Urmia, Iran", lat: 37.5527, lon: 45.0761 },
  { name: "Rasht, Iran", lat: 37.2809, lon: 49.5924 },
  { name: "Yazd, Iran", lat: 31.8974, lon: 54.3569 },
  { name: "Ardabil, Iran", lat: 38.2498, lon: 48.2933 },
  { name: "Bandar Abbas, Iran", lat: 27.1832, lon: 56.2666 },
  { name: "Arak, Iran", lat: 34.0954, lon: 49.7014 },
  { name: "Eslamshahr, Iran", lat: 35.5647, lon: 51.2353 },
  { name: "Zanjan, Iran", lat: 36.6736, lon: 48.4787 },
  { name: "Sanandaj, Iran", lat: 35.3219, lon: 46.9862 },
  { name: "Qazvin, Iran", lat: 36.2797, lon: 50.0049 },
  { name: "Kerman, Iran", lat: 30.2839, lon: 57.0834 },
  { name: "Hamadan, Iran", lat: 34.7983, lon: 48.5148 },
  { name: "Yasuj, Iran", lat: 30.6680, lon: 51.5880 },
  { name: "Bojnord, Iran", lat: 37.4761, lon: 57.3317 },
  { name: "Bushehr, Iran", lat: 28.9234, lon: 50.8203 },
  { name: "Gorgan, Iran", lat: 36.8427, lon: 54.4439 },
  { name: "Ilam, Iran", lat: 33.6374, lon: 46.4227 },
  { name: "Sari, Iran", lat: 36.5633, lon: 53.0601 },
  { name: "Shahrekord, Iran", lat: 32.3256, lon: 50.8644 }
];

// Bulletproof deduplication system
const uniqueCities = Array.from(
  new Map(majorCities.map(city => [city.name.toLowerCase().trim(), city])).values()
);

// Copy the timezone map from birthchartform
const cityTimezoneMap = {
  // USA
  "New York, NY, USA": "America/New_York",
  "Los Angeles, CA, USA": "America/Los_Angeles",
  "Chicago, IL, USA": "America/Chicago",
  "Houston, TX, USA": "America/Chicago",
  "Phoenix, AZ, USA": "America/Phoenix",
  "Philadelphia, PA, USA": "America/New_York",
  "San Antonio, TX, USA": "America/Chicago",
  "San Diego, CA, USA": "America/Los_Angeles",
  "Dallas, TX, USA": "America/Chicago",
  "San Jose, CA, USA": "America/Los_Angeles",
  
  // Europe
  "London, UK": "Europe/London",
  "Paris, France": "Europe/Paris",
  "Berlin, Germany": "Europe/Berlin",
  "Madrid, Spain": "Europe/Madrid",
  "Rome, Italy": "Europe/Rome",
  "Amsterdam, Netherlands": "Europe/Amsterdam",
  "Vienna, Austria": "Europe/Vienna",
  "Prague, Czech Republic": "Europe/Prague",
  "Budapest, Hungary": "Europe/Budapest",
  "Warsaw, Poland": "Europe/Warsaw",
  "Moscow, Russia": "Europe/Moscow",
  
  // Asia
  "Tokyo, Japan": "Asia/Tokyo",
  "Seoul, South Korea": "Asia/Seoul",
  "Beijing, China": "Asia/Shanghai",
  "Shanghai, China": "Asia/Shanghai",
  "Hong Kong": "Asia/Hong_Kong",
  "Singapore": "Asia/Singapore",
  "Bangkok, Thailand": "Asia/Bangkok",
  "Jakarta, Indonesia": "Asia/Jakarta",
  "Kuala Lumpur, Malaysia": "Asia/Kuala_Lumpur",
  "Manila, Philippines": "Asia/Manila",
  "Ho Chi Minh City, Vietnam": "Asia/Ho_Chi_Minh",
  "Mumbai, India": "Asia/Kolkata",
  "Delhi, India": "Asia/Kolkata",
  "Bangalore, India": "Asia/Kolkata",
  "Chennai, India": "Asia/Kolkata",
  "Kolkata, India": "Asia/Kolkata",
  "Hyderabad, India": "Asia/Kolkata",
  "Pune, India": "Asia/Kolkata",
  "Ahmedabad, India": "Asia/Kolkata",
  
  // Australia
  "Sydney, Australia": "Australia/Sydney",
  "Melbourne, Australia": "Australia/Melbourne",
  "Brisbane, Australia": "Australia/Brisbane",
  "Perth, Australia": "Australia/Perth",
  "Adelaide, Australia": "Australia/Adelaide",
  
  // Canada
  "Toronto, Canada": "America/Toronto",
  "Montreal, Canada": "America/Montreal",
  "Vancouver, Canada": "America/Vancouver",
  "Calgary, Canada": "America/Edmonton",
  "Edmonton, Canada": "America/Edmonton",
  "Ottawa, Canada": "America/Toronto",
  
  // Mexico
  "Mexico City, Mexico": "America/Mexico_City",
  "Guadalajara, Mexico": "America/Mexico_City",
  "Monterrey, Mexico": "America/Mexico_City",
  "Puebla, Mexico": "America/Mexico_City",
  
  // Brazil
  "São Paulo, Brazil": "America/Sao_Paulo",
  "Rio de Janeiro, Brazil": "America/Sao_Paulo",
  "Brasília, Brazil": "America/Sao_Paulo",
  "Salvador, Brazil": "America/Bahia",
  "Fortaleza, Brazil": "America/Fortaleza",
  "Belo Horizonte, Brazil": "America/Sao_Paulo",
  
  // South America
  "Buenos Aires, Argentina": "America/Argentina/Buenos_Aires",
  "Córdoba, Argentina": "America/Argentina/Cordoba",
  "Rosario, Argentina": "America/Argentina/Buenos_Aires",
  "Santiago, Chile": "America/Santiago",
  "Valparaíso, Chile": "America/Santiago",
  "Lima, Peru": "America/Lima",
  "Arequipa, Peru": "America/Lima",
  "Bogotá, Colombia": "America/Bogota",
  "Medellín, Colombia": "America/Bogota",
  "Cali, Colombia": "America/Bogota",
  "Caracas, Venezuela": "America/Caracas",
  "Maracaibo, Venezuela": "America/Caracas",
  "Quito, Ecuador": "America/Guayaquil",
  "Guayaquil, Ecuador": "America/Guayaquil",
  "Montevideo, Uruguay": "America/Montevideo",
  "Asunción, Paraguay": "America/Asuncion",
  "La Paz, Bolivia": "America/La_Paz",
  "Santa Cruz, Bolivia": "America/La_Paz",
  
  // Africa
  "Cape Town, South Africa": "Africa/Johannesburg",
  "Johannesburg, South Africa": "Africa/Johannesburg",
  "Durban, South Africa": "Africa/Johannesburg",
  "Pretoria, South Africa": "Africa/Johannesburg",
  "Port Elizabeth, South Africa": "Africa/Johannesburg",
  "Cairo, Egypt": "Africa/Cairo",
  "Alexandria, Egypt": "Africa/Cairo",
  "Giza, Egypt": "Africa/Cairo",
  "Lagos, Nigeria": "Africa/Lagos",
  "Kano, Nigeria": "Africa/Lagos",
  "Ibadan, Nigeria": "Africa/Lagos",
  "Kaduna, Nigeria": "Africa/Lagos",
  "Port Harcourt, Nigeria": "Africa/Lagos",
  "Nairobi, Kenya": "Africa/Nairobi",
  "Mombasa, Kenya": "Africa/Nairobi",
  "Kisumu, Kenya": "Africa/Nairobi",
  "Addis Ababa, Ethiopia": "Africa/Addis_Ababa",
  "Dar es Salaam, Tanzania": "Africa/Dar_es_Salaam",
  "Mwanza, Tanzania": "Africa/Dar_es_Salaam",
  "Kampala, Uganda": "Africa/Kampala",
  "Khartoum, Sudan": "Africa/Khartoum",
  "Casablanca, Morocco": "Africa/Casablanca",
  "Rabat, Morocco": "Africa/Casablanca",
  "Fez, Morocco": "Africa/Casablanca",
  "Marrakech, Morocco": "Africa/Casablanca",
  "Tunis, Tunisia": "Africa/Tunis",
  "Sfax, Tunisia": "Africa/Tunis",
  "Algiers, Algeria": "Africa/Algiers",
  "Oran, Algeria": "Africa/Algiers",
  "Tehran, Iran": "Asia/Tehran",
  "Mashhad, Iran": "Asia/Tehran",
  "Isfahan, Iran": "Asia/Tehran",
  "Tabriz, Iran": "Asia/Tehran",
  "Shiraz, Iran": "Asia/Tehran",
  "Karaj, Iran": "Asia/Tehran",
  "Qom, Iran": "Asia/Tehran",
  "Kermanshah, Iran": "Asia/Tehran",
  "Urmia, Iran": "Asia/Tehran",
  "Rasht, Iran": "Asia/Tehran",
  "Yazd, Iran": "Asia/Tehran",
  "Ardabil, Iran": "Asia/Tehran",
  "Bandar Abbas, Iran": "Asia/Tehran",
  "Arak, Iran": "Asia/Tehran",
  "Eslamshahr, Iran": "Asia/Tehran",
  "Zanjan, Iran": "Asia/Tehran",
  "Sanandaj, Iran": "Asia/Tehran",
  "Qazvin, Iran": "Asia/Tehran",
  "Kerman, Iran": "Asia/Tehran",
  "Hamadan, Iran": "Asia/Tehran",
  "Yasuj, Iran": "Asia/Tehran",
  "Bojnord, Iran": "Asia/Tehran",
  "Bushehr, Iran": "Asia/Tehran",
  "Gorgan, Iran": "Asia/Tehran",
  "Ilam, Iran": "Asia/Tehran",
  "Sari, Iran": "Asia/Tehran",
  "Shahrekord, Iran": "Asia/Tehran"
};

// Timezone utility functions
const getTimezoneFromCoordinates = (lat, lon) => {
  // Simple timezone estimation based on longitude
  // This is a basic approximation - in production you'd want a more sophisticated approach
  const timezoneOffset = Math.round(lon / 15);
  const timezoneName = `UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`;
  return timezoneName;
};

const getTimezoneFromCity = (cityName) => {
  return cityTimezoneMap[cityName] || 'UTC';
};

const AscendantDisplay = () => {
  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    latitude: '',
    longitude: '',
    timeZone: ''
  });
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [page, setPage] = useState(0);
  
  // React-select state
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  
  // Advanced settings state
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [useCoordinates, setUseCoordinates] = useState(false);
  const [coordinateError, setCoordinateError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSaveOption, setShowSaveOption] = useState(false);
  const [savedBirthData, setSavedBirthData] = useState([]);
  const [selectedSavedData, setSelectedSavedData] = useState(null);
  const { db } = useFirebase();
  const { user } = useAuth();
  
  // Timezone change notification state
  const [timezoneNotification, setTimezoneNotification] = useState(null);
  const [previousTimezone, setPreviousTimezone] = useState(null);

  // Save modal state
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Create options from predefined cities
  useEffect(() => {
    const predefinedOptions = uniqueCities.map(city => ({
      value: city.name,
      label: city.name,
      lat: city.lat,
      lon: city.lon,
      timezone: getTimezoneFromCity(city.name, formData.birthDate),
      source: 'predefined'
    }));
    setLocationOptions(predefinedOptions);
  }, [formData.birthDate]);

  // Function to show timezone change notification
  const showTimezoneNotification = (oldTimezone, newTimezone, location, date) => {
    if (oldTimezone && newTimezone && oldTimezone !== newTimezone && location && date) {
      // Format the date nicely
      const formattedDate = new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      // Determine if it's a DST change
      const isDSTChange = Math.abs(parseInt(oldTimezone.replace('UTC', '')) - parseInt(newTimezone.replace('UTC', ''))) === 1;
      
      const notification = {
        type: 'timezone-change',
        message: isDSTChange 
          ? `Daylight Saving Time detected for ${location} on ${formattedDate}: ${oldTimezone} → ${newTimezone}`
          : `Timezone updated for ${location} on ${formattedDate}: ${oldTimezone} → ${newTimezone}`,
        oldTimezone,
        newTimezone,
        location,
        date: formattedDate,
        isDSTChange
      };
      setTimezoneNotification(notification);
      
      // Auto-hide after 6 seconds for DST changes, 4 seconds for regular changes
      setTimeout(() => {
        setTimezoneNotification(null);
      }, isDSTChange ? 6000 : 4000);
    }
  };

  // Handle location search with react-select
  const handleLocationSearch = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      return Promise.resolve([]);
    }

    setIsLoadingOptions(true);
    
    return new Promise((resolve) => {
      try {
        // Search through predefined cities first
        const predefinedMatches = uniqueCities
          .filter(city => city.name.toLowerCase().includes(inputValue.toLowerCase()))
          .map(city => ({
            value: city.name,
            label: city.name,
            lat: city.lat,
            lon: city.lon,
            timezone: getTimezoneFromCity(city.name, formData.birthDate),
            source: 'predefined'
          }));

        // Combine and return options
        const allOptions = [...predefinedMatches];
        resolve(allOptions.slice(0, 10));
      } catch (error) {
        console.error('Location search error:', error);
        resolve([]);
      } finally {
        setIsLoadingOptions(false);
      }
    });
  }, []);

  // Handle location selection
  const handleLocationChange = async (selectedOption, actionMeta) => {
    if (actionMeta.action === 'clear') {
      setSelectedLocation(null);
      setFormData(prev => ({
        ...prev,
        birthPlace: '',
        latitude: '',
        longitude: '',
        timeZone: ''
      }));
      setShowCoordinates(false);
      return;
    }

    if (!selectedOption) return;

    setSelectedLocation(selectedOption);

    // If it's a predefined city
    if (selectedOption.lat && selectedOption.lon) {
      const oldTimezone = formData.timeZone;
      // Always recalculate timezone to ensure it's based on the current birth date
      const newTimezone = getTimezoneFromCity(selectedOption.value, formData.birthDate);
      
      setFormData(prev => ({
        ...prev,
        birthPlace: selectedOption.value,
        latitude: selectedOption.lat.toString(),
        longitude: selectedOption.lon.toString(),
        timeZone: newTimezone
      }));
      setShowCoordinates(true);
      
      // Show timezone change notification if applicable
      showTimezoneNotification(oldTimezone, newTimezone, selectedOption.value, formData.birthDate);
    }
  };

  // Custom option component to show timezone info
  const CustomOption = ({ data, innerProps, isFocused }) => (
    <div
      {...innerProps}
      style={{
        padding: '8px 12px',
        backgroundColor: isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}
    >
      <MapPin size={14} style={{ opacity: 0.7, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{data.label}</div>
        {data.timezone && (
          <div style={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            fontSize: '11px', 
            marginTop: '3px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '2px 6px',
            borderRadius: '3px',
            display: 'inline-block',
            fontFamily: 'monospace'
          }}>
            🌍 {data.timezone}
          </div>
        )}
      </div>
    </div>
  );

  // Custom no options message
  const NoOptionsMessage = ({ inputValue }) => (
    <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
      {inputValue.length < 2 ? 'Start typing to search cities...' : 'No cities found'}
    </div>
  );

  // Custom loading message
  const LoadingMessage = () => (
    <div style={{ padding: '8px 12px', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
      <Search size={14} style={{ marginRight: '8px', display: 'inline' }} />
      Searching...
    </div>
  );

  // Custom single value component to show timezone
  const CustomSingleValue = ({ data }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <MapPin size={14} style={{ opacity: 0.7, flexShrink: 0 }} />
      <div>
        <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{data.label}</div>
        {data.timezone && (
          <div style={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            fontSize: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            padding: '1px 4px',
            borderRadius: '2px',
            fontFamily: 'monospace'
          }}>
            {data.timezone}
          </div>
        )}
      </div>
    </div>
  );

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      minHeight: '42px',
      boxShadow: state.isFocused ? '0 0 0 1px rgba(255, 255, 255, 0.5)' : 'none',
      '&:hover': {
        border: '1px solid rgba(255, 255, 255, 0.5)'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#2c3e50',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '4px',
      zIndex: 1000
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
      color: 'white',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }),
    input: (provided) => ({
      ...provided,
      color: 'white'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)'
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)'
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.6)'
    })
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [name]: value };
      
      // If birth date changed and we have a location selected, update timezone
      if (name === 'birthDate' && newData.birthPlace) {
        const selectedCity = uniqueCities.find(city => city.name === newData.birthPlace);
        if (selectedCity) {
          try {
            const newTimezone = getTimezoneFromCity(selectedCity.name, value);
            
            // Show timezone change notification
            showTimezoneNotification(newData.timeZone, newTimezone, selectedCity.name, value);
            
            return { ...newData, timeZone: newTimezone };
          } catch (error) {
            console.error('Error updating timezone for date change:', error);
          }
        }
      }
      
      // Check if all required fields are filled to show save option
      const allFieldsFilled = newData.birthDate && 
                             newData.birthTime && 
                             newData.birthPlace;
      setShowSaveOption(allFieldsFilled);
      
      // Debug: Log when save option should show
      if (allFieldsFilled) {
      }
      
      return newData;
    });
  };

  const handleSaveChart = () => {
    if (!chartData) {
      setError('No chart data to save. Please generate a birth chart first.');
      return;
    }
    
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    try {
      // Format the reading for journal
      const journalEntry = {
        type: 'ascendant-chart',
        title: `Ascendant Chart - ${new Date().toLocaleDateString()}`,
        content: `Ascendant chart generated on ${new Date().toLocaleDateString()}`,
        details: {
          birthDate: formData.birthDate,
          birthTime: formData.birthTime,
          birthPlace: formData.birthPlace,
          latitude: formData.latitude,
          longitude: formData.longitude,
          timeZone: formData.timeZone,
          chartData: chartData
        },
        timestamp: new Date().toISOString()
      };
      
      // Save to localStorage for backup
      const savedReadings = JSON.parse(localStorage.getItem('ascendantChartSavedReadings') || '[]');
      const newReading = {
        id: Date.now(),
        date: new Date().toISOString(),
        chartData: chartData,
        formData: formData,
        type: 'ascendant-chart'
      };
      
      const updatedReadings = [...savedReadings, newReading];
      localStorage.setItem('ascendantChartSavedReadings', JSON.stringify(updatedReadings));
      
      // Save to Firebase journal if user is logged in
        if (window.firebase && window.firebase.auth && window.firebase.auth().currentUser) {
          const user = window.firebase.auth().currentUser;
          const entriesRef = window.firebase
            .firestore()
            .collection('journal')
            .doc(user.uid)
            .collection('entries');
          await entriesRef.add({
            userId: user.uid,
            ...journalEntry
          });
        }
      
      setSaveSuccess(true);
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowSaveModal(false);
        setSaveSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving chart:', error);
      alert('Error saving chart. Please try again.');
    }
  };

  // Load saved birth data from Firestore
  useEffect(() => {
    let isMounted = true;
    const loadBirthData = async () => {
      if (!user?.uid) {
        setSavedBirthData([]);
        return;
      }
      try {
        const ref = collection(db, 'users', user.uid, 'birthData');
        const snapshot = await getDocs(ref);
        if (!isMounted) return;
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSavedBirthData(items);
      } catch (err) {
        console.error('Error loading saved birth data:', err);
      }
    };
    loadBirthData();
    return () => { isMounted = false; };
  }, [db, user]);

  const handleSaveBirthData = () => {
    const birthDataToSave = {
      id: Date.now(),
      birthDate: formData.birthDate,
      birthTime: formData.birthTime,
      birthPlace: formData.birthPlace,
      latitude: formData.latitude,
      longitude: formData.longitude,
      timeZone: formData.timeZone,
      savedAt: new Date().toISOString()
    };
    
    setSavedBirthData(prev => [...prev, birthDataToSave]);

    if (!user?.uid) {
      setTimezoneNotification({
        type: 'auth-required',
        message: 'Please sign in to sync your birth info across devices. Saved locally for now.',
        duration: 4000
      });
      setTimeout(() => setTimezoneNotification(null), 4000);
      return;
    }

    const persist = async () => {
      try {
        const ref = collection(db, 'users', user.uid, 'birthData');
        const docRef = await addDoc(ref, {
          birthDate: birthDataToSave.birthDate,
          birthTime: birthDataToSave.birthTime,
          birthPlace: birthDataToSave.birthPlace,
          latitude: birthDataToSave.latitude,
          longitude: birthDataToSave.longitude,
          timeZone: birthDataToSave.timeZone,
          savedAt: serverTimestamp()
        });

        setSavedBirthData(prev => [
          ...prev.filter(item => item.id !== birthDataToSave.id),
          { ...birthDataToSave, id: docRef.id }
        ]);
        setSaveSuccess(true);

        setTimezoneNotification({
          type: 'save-success',
          message: `Birth info saved successfully! You can now load it from the dropdown above.`,
          duration: 4000
        });
        setTimeout(() => {
          setSaveSuccess(false);
          setTimezoneNotification(null);
        }, 4000);
      } catch (error) {
        console.error('Error saving birth data:', error);
        setTimezoneNotification({
          type: 'error',
          message: 'Error saving to your account. Keeping the data locally—please try again.',
          duration: 4000
        });
        setTimeout(() => setTimezoneNotification(null), 4000);
      }
    };

    persist();
  };

  const handleLoadSavedData = (savedData) => {
    setFormData({
      birthDate: savedData.birthDate,
      birthTime: savedData.birthTime,
      birthPlace: savedData.birthPlace,
      latitude: savedData.latitude,
      longitude: savedData.longitude,
      timeZone: savedData.timeZone
    });
    setSelectedSavedData(savedData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const latitude = parseFloat(formData.latitude) || 0;
      const longitude = parseFloat(formData.longitude) || 0;
      const { birthDate, birthTime, birthPlace } = formData;
      if (!birthDate || !birthTime || !birthPlace) throw new Error('Please enter all required fields.');
      
      let timezoneName = cityTimezoneMap[birthPlace] || 'UTC';
      
      // Create local time properly
      const local = DateTime.fromISO(`${birthDate}T${birthTime}`, { zone: timezoneName });
      if (!local.isValid) throw new Error('Invalid date or time format.');
      
      // Check for timezone changes (DST, etc.)
      const utc = local.toUTC();
      const timezoneInfo = local.zone;
      const isDST = timezoneInfo.isLuxonZone ? timezoneInfo.isDST(local.ts) : false;
      
      // Show timezone change notification if applicable
      if (isDST) {
        const notification = {
          type: 'timezone-change',
          message: `Daylight Saving Time (DST) was in effect for ${birthPlace} on ${birthDate}. Your birth time has been adjusted accordingly.`,
          oldTimezone: 'Standard Time',
          newTimezone: 'Daylight Saving Time',
          location: birthPlace,
          date: birthDate,
          isDSTChange: true
        };
        setTimezoneNotification(notification);
        
        // Auto-hide after 6 seconds
        setTimeout(() => {
          setTimezoneNotification(null);
        }, 6000);
      }
      
      // Calculate birth chart
      const chart = await calculateBirthChartWithLibrary(
        local.year,
        local.month - 1, // Convert to 0-indexed for CircularNatalHoroscopeJS
        local.day,
        local.hour,
        local.minute,
        local.second,
        latitude,
        longitude,
        false, // showModernPlanets
        'whole-sign' // houseSystem
      );
      
      setChartData({
        ...chart,
        birthDate: birthDate,
        birthTime: birthTime,
        birthPlace: birthPlace
      });
      setPage(1);
    } catch (err) {
      setError("An error occurred while generating the ascendant chart. Please check your data and try again. " + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const getSignFromLongitude = (longitude) => {
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    const signIndex = Math.floor(longitude / 30);
    return signs[signIndex];
  };

  const getSignEmoji = (sign) => {
    const signEmojis = {
      'Aries': '♈',
      'Taurus': '♉',
      'Gemini': '♊',
      'Cancer': '♋',
      'Leo': '♌',
      'Virgo': '♍',
      'Libra': '♎',
      'Scorpio': '♏',
      'Sagittarius': '♐',
      'Capricorn': '♑',
      'Aquarius': '♒',
      'Pisces': '♓'
    };
    return signEmojis[sign] || '⭐';
  };

  const formatLongitude = (longitude) => {
    const signIndex = Math.floor(longitude / 30);
    const degreeInSign = longitude % 30;
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    return `${Math.floor(degreeInSign)}° ${signs[signIndex]}`;
  };

  return (
    <div
      className="full-screen-image-container full-height margin-top-0"
      style={{
        background: 'linear-gradient(135deg, #2d1b2e 0%, #4a2545 50%, #3d2438 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 80%, rgba(255, 215, 0, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(255, 20, 147, 0.05) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <div className="ascendant-display-container">
        <div className="ascendant-header" style={{ marginBottom: '0.5rem' }}>
          <div className="ascendant-title">
            <Sparkles size={24} />
            <h1 style={{ fontSize: '1.5rem' }}>Ascendant Calculator</h1>
          </div>
        </div>

        {/* Timezone Change Notification */}
        {timezoneNotification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: `2px solid ${timezoneNotification.type === 'save-success' ? '#28a745' : timezoneNotification.isDSTChange ? '#ff6b35' : '#ffd700'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 1000,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            animation: 'slideInRight 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{ 
                width: '8px', 
                height: '8px', 
                backgroundColor: timezoneNotification.type === 'save-success' ? '#28a745' : timezoneNotification.isDSTChange ? '#ff6b35' : '#ffd700', 
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></div>
              <span style={{ 
                color: timezoneNotification.type === 'save-success' ? '#28a745' : timezoneNotification.isDSTChange ? '#ff6b35' : '#ffd700', 
                fontWeight: '600' 
              }}>
                {timezoneNotification.type === 'save-success' ? '✅ Saved Successfully' : timezoneNotification.isDSTChange ? 'DST Detected' : 'Timezone Updated'}
              </span>
            </div>
            <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
              {timezoneNotification.message}
            </div>
            <button
              onClick={() => setTimezoneNotification(null)}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'none',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        )}

      {!chartData && (
        <form onSubmit={handleSubmit} className="ascendant-form" style={{ padding: '0.5rem', marginBottom: '0.4rem' }}>
          {/* Load Previous Birth Info - Comes first for users who already have saved data */}
          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <User size={14} />
              <span style={{ fontSize: '0.85rem' }}>Load Previous Birth Info</span>
            </div>
            <div className="form-field">
              <select
                value=""
                onChange={(e) => {
                  const selected = savedBirthData.find(data => data.id === e.target.value || data.id === parseInt(e.target.value));
                  if (selected) {
                    handleLoadSavedData(selected);
                  }
                }}
                style={{
                  padding: '0 0.6rem',
                  fontSize: '0.85rem',
                  width: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  color: 'white'
                }}
              >
                <option value="">Select previously saved birth info...</option>
                {savedBirthData.map(data => (
                  <option key={data.id} value={data.id}>
                    {[data.name, data.birthDate, data.birthTime, data.birthPlace].filter(Boolean).join(' - ')}
                  </option>
                ))}
              </select>
              <small style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Loads your saved birth info from your account.
              </small>
            </div>
          </div>

          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <Calendar size={14} />
              <span style={{ fontSize: '0.85rem' }}>Birth Details</span>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label style={{ fontSize: '0.75rem' }}>Birth Date</label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  style={{ 
                    padding: '0 0.6rem', 
                    fontSize: '0.85rem'
                  }}
                />
              </div>
              <div className="form-field">
                <label style={{ fontSize: '0.75rem' }}>Birth Time</label>
                <input
                  type="time"
                  name="birthTime"
                  value={formData.birthTime}
                  onChange={handleChange}
                  required
                  style={{ 
                    padding: '0 0.6rem', 
                    fontSize: '0.85rem'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="form-section" style={{ marginBottom: '0.5rem', padding: '0.6rem' }}>
            <div className="section-title">
              <MapPin size={14} />
              <span style={{ fontSize: '0.85rem' }}>Birth Location</span>
            </div>
            
            <div className="form-field">
              <AsyncSelect
                value={selectedLocation}
                onChange={handleLocationChange}
                loadOptions={handleLocationSearch}
                placeholder="Select your city from the list or start typing to search..."
                isClearable
                isSearchable
                isLoading={isLoadingOptions}
                loadingMessage={LoadingMessage}
                noOptionsMessage={NoOptionsMessage}
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSingleValue
                }}
                styles={customSelectStyles}
                cacheOptions={false}
                defaultOptions={locationOptions}
              />
              <small style={{ marginTop: '0.5rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Search from 200+ major cities or enter any location worldwide
              </small>
            </div>
            
            {showCoordinates && (
              <div className="form-field">
                <label>Time Zone</label>
                <input
                  type="text"
                  name="timeZone"
                  value={formData.timeZone}
                  onChange={handleChange}
                  placeholder="e.g., UTC+1"
                  required
                  readOnly
                  style={{ 
                    padding: '0 0.6rem', 
                    fontSize: '0.85rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '4px',
                    color: 'white'
                  }}
                />
                <small>Automatically calculated from location and date</small>
              </div>
            )}
          </div>





          <div className="form-actions" style={{ marginTop: 'auto', paddingTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', flex: '1', minHeight: '0' }}>
            <button
              type="button"
              onClick={handleSaveBirthData}
              disabled={!showSaveOption}
              style={{ 
                padding: '0.5rem 1rem', 
                fontSize: '0.75rem',
                borderRadius: '8px',
                border: 'none',
                cursor: showSaveOption ? 'pointer' : 'not-allowed',
                background: showSaveOption 
                  ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                  : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                color: 'white',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                opacity: showSaveOption ? 1 : 0.6,
                transform: showSaveOption ? 'translateY(0)' : 'translateY(0)',
                boxShadow: showSaveOption 
                  ? '0 4px 12px rgba(40, 167, 69, 0.3)' 
                  : '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}
              onMouseEnter={(e) => {
                if (showSaveOption) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (showSaveOption) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)';
                }
              }}
            >
              <Save size={16} style={{ marginRight: '0.5rem' }} />
              {showSaveOption ? 'Save Birth Info' : 'Fill All Fields to Save'}
            </button>
            
            <button
              type="submit"
              className="calculate-button"
              disabled={loading}
              style={{ padding: '0.5rem 1rem', fontSize: '0.75rem' }}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spinning" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  Generate Ascendant Chart
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="error-message">
          <Info size={16} />
          {error}
        </div>
      )}

      {chartData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="ascendant-display-container"
        >
        <div className="ascendant-result">
          <div className="result-header">
            <h2>Your Ascendant</h2>
            <div className="ascendant-sign-display">
              <span className="sign-emoji">{getSignEmoji(getSignFromLongitude(chartData.ascendantLongitude))}</span>
              <span className="sign-name">{getSignFromLongitude(chartData.ascendantLongitude)}</span>
            </div>
          </div>

          <div className="result-details">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              margin: '1.5rem 0 2.5rem 0',
              gap: '1.5rem',
            }}>
              <div style={{
                flex: 1,
                padding: '1rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                color: '#fff',
                fontWeight: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <span style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Birth Date</span>
                <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>{chartData.birthDate}</span>
              </div>
              <div style={{
                flex: 1,
                padding: '1rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                color: '#fff',
                fontWeight: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <span style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Birth Place</span>
                <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>{chartData.birthPlace}</span>
              </div>
              <div style={{
                flex: 1,
                padding: '1rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                color: '#fff',
                fontWeight: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <span style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '0.25rem' }}>Birth Time</span>
                <span style={{ fontWeight: 700, fontSize: '1.15rem' }}>{chartData.birthTime}</span>
              </div>
            </div>
          </div>

          <div className="ascendant-description">
            <h3>What is the Ascendant?</h3>
            <p>
              The Ascendant, or Rising Sign, represents the zodiac sign that was rising on the eastern horizon
              at the exact moment of your birth. It's often called the "mask" you wear and how others see you.
              Your Ascendant influences your physical appearance, first impressions, and the way you approach new situations.
            </p>
          </div>
        </div>

          <motion.button
            className="button is-warning"
            style={{
              position: "fixed",
              top: 100,
              left: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={() => {
              setPage(0);
              setChartData(null);
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <ArrowLeft size={20} />
            Back
          </motion.button>

          {/* Save Chart Button - Fixed Position (Same as Celtic Cross) */}
          <button 
            className="save-chart-button" 
            onClick={handleSaveChart}
            style={{
              position: "fixed",
              top: 150,
              right: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #E53E3E 0%, #C53030 100%)",
              color: "#fff",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <Save size={16} />
            Save Chart
          </button>
        </motion.div>
      )}

      {/* Save to Journal Modal */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #2d1b2e 0%, #4a2545 50%, #3d2438 100%)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            position: 'relative'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowSaveModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                color: '#FFD700',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ✕
            </button>

            {/* Modal Content */}
            <div style={{ textAlign: 'center' }}>
              {!saveSuccess ? (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    📖
                  </div>
                  
                  <h2 style={{
                    color: '#FFD700',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '16px',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6347 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Save to Journal
                  </h2>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    marginBottom: '24px'
                  }}>
                    Save your ascendant chart reading to your journal?
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    ✅
                  </div>
                  
                  <h2 style={{
                    color: '#28a745',
                    fontSize: '24px',
                    fontWeight: '700',
                    marginBottom: '16px'
                  }}>
                    Chart Saved!
                  </h2>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '16px',
                    lineHeight: '1.6'
                  }}>
                    Your ascendant chart has been successfully saved to your journal.
                  </p>
                </>
              )}

              {!saveSuccess && (
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setShowSaveModal(false)}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      background: 'transparent',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'rgba(255, 215, 0, 0.5)';
                      e.target.style.color = '#FFD700';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    }}
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={confirmSave}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                      color: '#1a1a2e',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(255, 215, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.3)';
                    }}
                  >
                    💾 Save to Journal
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AscendantDisplay; 
